import { GEMINI_API_KEY, GEMINI_CONFIG } from '../config/gemini.js';
import { aspectRatioOptions } from '../data/vehicleData.js';

export class ImageGeneratorService {
  constructor() {
    this.apiKey = GEMINI_API_KEY;
    this.baseUrl = GEMINI_CONFIG.apiEndpoint;
  }

  async generateImage(prompt, aspectRatio = 'landscape') {
    console.log('🎨 Starting image generation with prompt:', prompt);
    console.log('📐 Aspect ratio:', aspectRatio);
    console.log('🔑 Using API Key:', this.apiKey ? `${this.apiKey.substring(0, 10)}...` : 'MISSING');
    console.log('🌐 Using endpoint:', this.baseUrl);
    
    try {
      // Try multiple approaches to ensure we get an image
      let response = await this.attemptOptimizedImageGeneration(prompt, aspectRatio);
      
      if (response.success && response.imageData && !response.isPlaceholder) {
        console.log('✅ Successfully generated real image with base64 data');
        return response;
      }
      
      // If first attempt didn't work, try alternative format
      console.log('🔄 First attempt returned no image data, trying alternative format...');
      response = await this.attemptAlternativeImageGeneration(prompt, aspectRatio);
      
      if (response.success && response.imageData && !response.isPlaceholder) {
        console.log('✅ Successfully generated real image with alternative format');
        return response;
      }
      
      console.log('⚠️ Using placeholder - API responded but no image data found');
      return this.createEnhancedPlaceholder(prompt, response.apiResponse, aspectRatio);

    } catch (error) {
      console.error('❌ Error in image generation:', error);
      return {
        success: false,
        error: error.message,
        prompt: prompt,
        imageUrl: this.generateErrorPlaceholder(aspectRatio),
        apiResponse: error.response ? JSON.stringify(error.response, null, 2) : null,
        isPlaceholder: true,
        source: 'error'
      };
    }
  }

  async attemptOptimizedImageGeneration(prompt, aspectRatio) {
    try {
      const aspectRatioInfo = aspectRatioOptions.find(option => option.id === aspectRatio);
      const enhancedPrompt = this.enhancePromptWithAspectRatio(prompt, aspectRatioInfo);

      // Optimized request format specifically for image generation
      const requestBody = {
        contents: [{
          parts: [{
            text: enhancedPrompt
          }]
        }],
        generationConfig: {
          temperature: 0.4,
          topK: 32,
          topP: 1,
          maxOutputTokens: 4096,
        },
        safetySettings: [
          {
            category: "HARM_CATEGORY_HARASSMENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_HATE_SPEECH", 
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_DANGEROUS_CONTENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          }
        ]
      };

      console.log('📡 Making optimized API request to:', this.baseUrl);
      console.log('📋 Request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(`${this.baseUrl}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        
        return {
          success: false,
          error: `API Error: ${response.status} - ${errorData.error?.message || errorText}`,
          apiResponse: errorText
        };
      }

      const data = await response.json();
      console.log('📦 Full API Response:', JSON.stringify(data, null, 2));

      return this.parseImageResponse(data, prompt, 'optimized-format', aspectRatio);

    } catch (error) {
      console.error('❌ Error in attemptOptimizedImageGeneration:', error);
      throw error;
    }
  }

  async attemptAlternativeImageGeneration(prompt, aspectRatio) {
    try {
      const aspectRatioInfo = aspectRatioOptions.find(option => option.id === aspectRatio);
      const enhancedPrompt = this.enhancePromptWithAspectRatio(prompt, aspectRatioInfo);

      // Alternative format with explicit image generation instruction
      const requestBody = {
        contents: [{
          parts: [{
            text: `Create a high-quality image: ${enhancedPrompt}`
          }]
        }],
        generationConfig: {
          temperature: 0.6,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 8192,
        }
      };

      console.log('📡 Making alternative API request...');

      const response = await fetch(`${this.baseUrl}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Alternative API Error:', errorText);
        return {
          success: false,
          error: `Alternative API Error: ${response.status}`,
          apiResponse: errorText
        };
      }

      const data = await response.json();
      console.log('📦 Alternative API Response:', JSON.stringify(data, null, 2));

      return this.parseImageResponse(data, prompt, 'alternative-format', aspectRatio);

    } catch (error) {
      console.error('❌ Error in attemptAlternativeImageGeneration:', error);
      return {
        success: false,
        error: error.message,
        apiResponse: null
      };
    }
  }

  enhancePromptWithAspectRatio(prompt, aspectRatioInfo) {
    if (!aspectRatioInfo) return prompt;

    let enhancedPrompt = prompt;

    // Add aspect ratio specific instructions with proper dimensions
    switch (aspectRatioInfo.id) {
      case 'landscape':
        enhancedPrompt += ` Composed in wide landscape format with ${aspectRatioInfo.width}x${aspectRatioInfo.height} pixel dimensions (aspect ratio ${aspectRatioInfo.ratio}). Horizontal composition with expansive background perfect for web banners and social media posts.`;
        break;
      case 'square':
        enhancedPrompt += ` Composed in perfect square format with ${aspectRatioInfo.width}x${aspectRatioInfo.height} pixel dimensions (aspect ratio ${aspectRatioInfo.ratio}). Centered composition with balanced framing ideal for Instagram posts.`;
        break;
      case 'portrait':
        enhancedPrompt += ` Composed in vertical portrait format with ${aspectRatioInfo.width}x${aspectRatioInfo.height} pixel dimensions (aspect ratio ${aspectRatioInfo.ratio}). Vertical composition with tall framing perfect for mobile displays and Instagram stories.`;
        break;
      case 'banner':
        enhancedPrompt += ` Composed in wide banner format with ${aspectRatioInfo.width}x${aspectRatioInfo.height} pixel dimensions (aspect ratio ${aspectRatioInfo.ratio}). Ultra-wide horizontal composition perfect for website headers.`;
        break;
      case 'print':
        enhancedPrompt += ` Composed in traditional print format with ${aspectRatioInfo.width}x${aspectRatioInfo.height} pixel dimensions (aspect ratio ${aspectRatioInfo.ratio}). Classic proportions with professional layout suitable for print advertising.`;
        break;
    }

    // Add dimension enforcement
    enhancedPrompt += ` Ensure the image maintains exactly ${aspectRatioInfo.width} by ${aspectRatioInfo.height} pixel dimensions with crisp, high-resolution quality.`;

    return enhancedPrompt;
  }

  parseImageResponse(data, prompt, source, aspectRatio) {
    // Check for image data in multiple possible locations
    if (data.candidates && data.candidates[0]) {
      const candidate = data.candidates[0];
      console.log('🔍 Checking candidate structure:', JSON.stringify(candidate, null, 2));

      if (candidate.content && candidate.content.parts) {
        for (const part of candidate.content.parts) {
          console.log('🔍 Checking part:', JSON.stringify(part, null, 2));
          
          // Primary location: inline_data
          if (part.inline_data && part.inline_data.data) {
            console.log('🖼️ Found inline image data! Processing...');
            return this.processImageData(part.inline_data, prompt, source, aspectRatio);
          }
          
          // Alternative location: inlineData (camelCase)
          if (part.inlineData && part.inlineData.data) {
            console.log('🖼️ Found camelCase inline image data! Processing...');
            return this.processImageData(part.inlineData, prompt, source, aspectRatio);
          }
          
          // Check for file data references
          if (part.file_data || part.fileData) {
            const fileData = part.file_data || part.fileData;
            console.log('📁 Found file data reference:', fileData);
            return {
              success: true,
              prompt: prompt,
              imageUrl: this.generateRealisticPlaceholder(prompt, aspectRatio),
              note: `API returned file reference: ${JSON.stringify(fileData)}. This may require additional processing.`,
              isPlaceholder: true,
              source: `${source}-file-ref`,
              fileReference: fileData,
              apiResponse: JSON.stringify(data, null, 2)
            };
          }
          
          // Check for text responses
          if (part.text) {
            console.log('📝 Text response received:', part.text);
            
            // Check if text contains image-related information
            if (part.text.toLowerCase().includes('image') || 
                part.text.toLowerCase().includes('generate') ||
                part.text.toLowerCase().includes('create')) {
              return {
                success: true,
                prompt: prompt,
                imageUrl: this.generateRealisticPlaceholder(prompt, aspectRatio),
                note: `API acknowledged image request but returned text: "${part.text}". The model may be in text-only mode.`,
                isPlaceholder: true,
                source: `${source}-text-response`,
                textResponse: part.text,
                apiResponse: JSON.stringify(data, null, 2)
              };
            }
          }
        }
      }
    }

    console.log('⚠️ No image data found in any expected location');
    return {
      success: true,
      prompt: prompt,
      imageUrl: this.generateRealisticPlaceholder(prompt, aspectRatio),
      note: 'API responded successfully but no image data was found in any expected format. Check the API response for details.',
      isPlaceholder: true,
      source: `${source}-no-image-data`,
      apiResponse: JSON.stringify(data, null, 2)
    };
  }

  processImageData(inlineData, prompt, source, aspectRatio) {
    try {
      const imageData = inlineData.data;
      const mimeType = inlineData.mime_type || inlineData.mimeType || 'image/png';
      
      // Validate base64 data
      if (!imageData || typeof imageData !== 'string') {
        throw new Error('Invalid image data format');
      }

      // Remove any data URL prefix if present
      const cleanBase64 = imageData.replace(/^data:[^;]+;base64,/, '');
      
      // Validate base64 format
      try {
        atob(cleanBase64);
      } catch (e) {
        throw new Error('Invalid base64 encoding');
      }
      
      // Create data URL for display
      const imageUrl = `data:${mimeType};base64,${cleanBase64}`;
      
      console.log('✅ Successfully processed image data');
      console.log(`📊 Image size: ${cleanBase64.length.toLocaleString()} characters`);
      console.log(`🎭 MIME type: ${mimeType}`);
      console.log(`💾 Estimated file size: ~${Math.round(cleanBase64.length * 0.75 / 1024)}KB`);
      
      return {
        success: true,
        prompt: prompt,
        imageUrl: imageUrl,
        imageData: cleanBase64,
        mimeType: mimeType,
        aspectRatio: aspectRatio,
        source: source,
        isPlaceholder: false,
        apiResponse: `Image successfully generated and processed. Size: ${cleanBase64.length} chars, Type: ${mimeType}`
      };
      
    } catch (error) {
      console.error('❌ Error processing image data:', error);
      return {
        success: false,
        error: `Failed to process image data: ${error.message}`,
        prompt: prompt,
        imageUrl: this.generateErrorPlaceholder(aspectRatio),
        isPlaceholder: true,
        source: `${source}-processing-error`
      };
    }
  }

  createEnhancedPlaceholder(prompt, apiResponse = null, aspectRatio = 'landscape') {
    console.log('🎨 Creating enhanced placeholder for:', prompt);
    return {
      success: true,
      prompt: prompt,
      imageUrl: this.generateRealisticPlaceholder(prompt, aspectRatio),
      note: 'API connected successfully but returned no image data. The model may need different parameters or may be in preview mode.',
      isPlaceholder: true,
      apiResponse: apiResponse,
      source: 'enhanced-placeholder'
    };
  }

  generateRealisticPlaceholder(prompt, aspectRatio = 'landscape') {
    // Extract vehicle info for better placeholder
    const vehicleMatch = prompt.match(/featuring a ([^,\.]+)/i) || 
                        prompt.match(/([A-Z][a-z]+ [A-Z][a-z]+)/i) ||
                        prompt.match(/(sedan|suv|truck|coupe|convertible|hatchback)/i);
    const vehicle = vehicleMatch ? vehicleMatch[1] : 'Vehicle';
    
    // Get dimensions based on aspect ratio
    const aspectRatioInfo = aspectRatioOptions.find(option => option.id === aspectRatio);
    const width = aspectRatioInfo ? aspectRatioInfo.width : 1200;
    const height = aspectRatioInfo ? aspectRatioInfo.height : 800;
    
    // Create a more realistic automotive placeholder
    const encodedText = encodeURIComponent(`${vehicle} Marketing Image`);
    
    // Use automotive color scheme
    const bgColor = '1e40af'; // Blue
    const textColor = 'ffffff'; // White
    
    return `https://via.placeholder.com/${width}x${height}/${bgColor}/${textColor}?text=${encodedText}`;
  }

  generateErrorPlaceholder(aspectRatio = 'landscape') {
    const aspectRatioInfo = aspectRatioOptions.find(option => option.id === aspectRatio);
    const width = aspectRatioInfo ? aspectRatioInfo.width : 1200;
    const height = aspectRatioInfo ? aspectRatioInfo.height : 800;
    
    return `https://via.placeholder.com/${width}x${height}/dc2626/ffffff?text=Image+Generation+Error`;
  }

  buildPrompt(config) {
    const {
      vehicle,
      scene,
      season,
      promotion,
      style,
      aspectRatio,
      customText,
      dealershipName,
      colors,
      textOverlays
    } = config;

    // Build a detailed, specific prompt optimized for image generation
    let prompt = `Professional automotive marketing photograph featuring a ${vehicle}`;

    // Add scene context with specific details
    if (scene && scene !== 'studio') {
      const sceneDescriptions = {
        showroom: 'inside a modern, well-lit car dealership showroom with polished concrete floors, contemporary lighting, and glass windows',
        city: 'in an urban setting with modern city skyline, clean streets, and architectural elements in the background',
        highway: 'on a scenic highway with beautiful landscape, open road, and natural lighting',
        luxury: 'in front of a luxury mansion or upscale hotel with elegant architecture and manicured landscaping',
        nature: 'in a natural setting with mountains, trees, and beautiful outdoor landscape',
        parking: 'in a well-organized, clean dealership parking lot with proper lighting and professional presentation',
        sunset: 'during golden hour with dramatic sunset lighting, warm atmospheric tones, and beautiful sky'
      };
      prompt += ` ${sceneDescriptions[scene] || `in a ${scene} setting`}`;
    }

    // Add seasonal atmosphere
    if (season) {
      const seasonDescriptions = {
        spring: 'with fresh spring atmosphere, blooming flowers, vibrant green foliage, and bright natural lighting',
        summer: 'with bright summer lighting, lush greenery, clear blue skies, and vibrant colors',
        autumn: 'with beautiful fall colors, golden and red foliage, warm lighting, and seasonal atmosphere',
        winter: 'with crisp winter atmosphere, possible light snow, clear lighting, and seasonal elements'
      };
      prompt += ` ${seasonDescriptions[season] || `during ${season} season`}`;
    }

    // Add vehicle color preferences
    if (colors && colors.length > 0) {
      prompt += `. The vehicle is ${colors[0].toLowerCase()} colored`;
    }

    // Add style-specific technical details
    const styleDetails = {
      photorealistic: 'Ultra-realistic photography with perfect lighting, sharp details, professional composition, and commercial quality',
      cinematic: 'Cinematic lighting with dramatic shadows, movie-like atmosphere, artistic composition, and depth of field',
      commercial: 'Professional automotive advertising style with clean composition, perfect product presentation, and marketing appeal',
      artistic: 'Creative and stylized approach with unique perspective, artistic flair, and innovative composition',
      minimalist: 'Clean, minimal composition with simple background, focus on the vehicle, and elegant simplicity'
    };

    prompt += `. ${styleDetails[style] || 'Professional automotive photography style'}`;

    // Add text overlay requirements
    if (textOverlays && textOverlays.length > 0) {
      const visibleOverlays = textOverlays.filter(overlay => overlay.visible);
      if (visibleOverlays.length > 0) {
        prompt += '. The image should have space and composition suitable for text overlays including:';
        visibleOverlays.forEach(overlay => {
          let overlayText = overlay.text;
          if (overlay.type === 'offer' && overlay.template && overlay.fields) {
            // Process offer template
            overlayText = overlay.template.template;
            overlay.template.fields.forEach(field => {
              const value = overlay.fields[field] || `[${field}]`;
              overlayText = overlayText.replace(`{{${field}}}`, value);
            });
          }
          
          const positionDesc = overlay.position.replace('-', ' ');
          prompt += ` "${overlayText}" positioned at ${positionDesc} with ${overlay.fontStyle} styling,`;
        });
        prompt = prompt.slice(0, -1); // Remove trailing comma
      }
    }

    // Add technical specifications for optimal image generation
    prompt += '. Shot with professional camera equipment, high resolution, excellent lighting, sharp focus throughout, automotive photography quality, perfect for dealership marketing and advertising use.';

    // Add promotional context if specified
    if (promotion && promotion !== 'none') {
      const promoContext = {
        sale: 'The composition should convey value and attractiveness suitable for a sales event',
        new: 'Emphasize the freshness and innovation of a new model launch',
        limited: 'Create an exclusive, premium feeling appropriate for limited availability',
        special: 'Highlight the special nature and desirability of this offering',
        financing: 'Professional presentation suitable for financing and payment promotions',
        lease: 'Clean, approachable presentation perfect for lease marketing campaigns',
        certified: 'Trustworthy, quality-focused presentation for certified pre-owned vehicles'
      };
      prompt += ` ${promoContext[promotion] || ''}`;
    }

    // Add dealership branding context
    if (dealershipName) {
      prompt += ` The overall aesthetic should reflect the premium quality and professionalism of ${dealershipName} dealership`;
    }

    // Add any custom requirements
    if (customText) {
      prompt += `. Additional specific requirements: ${customText}`;
    }

    return prompt;
  }
}

export const imageGenerator = new ImageGeneratorService();